import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlogPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogPosts();
  }, [fetchBlogPosts]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-page">
        <p className="font-roboto text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="blog-page">
      <section 
        className="section-spacing text-white relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/blog-header.jpg)',
          minHeight: '305px'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="content-container relative z-10">
          <h1 className="font-rubik text-4xl md:text-5xl font-bold text-center mb-4" data-testid="blog-page-title">
            BLOG & INSIGHTS
          </h1>
          <p className="font-roboto text-lg text-center max-w-3xl mx-auto">
            Stay informed with our latest insights on sustainable development, conservation,
            project management, and organizational capacity building.
          </p>
          <div className="mt-6 text-center">
            <Link to="/admin/blog" className="btn-secondary" data-testid="admin-blog-link">
              Manage Blog Posts
            </Link>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="content-container">
          {blogPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-roboto text-lg text-gray-600 mb-4">
                No blog posts yet. Create your first post!
              </p>
              <Link to="/admin/blog" className="btn-primary">
                Create Blog Post
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {blogPosts.map((post) => (
                <div
                  key={post.id}
                  className="bg-[#f5f5f5] rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  data-testid={`blog-post-${post.id}`}
                >
                  {post.image_url && (
                    <img 
                      src={post.image_url} 
                      alt={post.title} 
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <div className="mb-3">
                      <span className="inline-block bg-[#aaae10] text-white px-3 py-1 rounded-full font-roboto text-xs font-medium">
                        {post.category}
                      </span>
                    </div>
                    <h2 className="font-rubik text-xl font-bold mb-2 text-[#646464] uppercase">
                      {post.title}
                    </h2>
                    <p className="font-roboto text-sm text-[#646464] mb-4">{post.date}</p>
                    <p className="font-roboto text-base leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    {post.content && (
                      <Link 
                        to={`/blog/${post.id}`}
                        className="btn-secondary text-sm" 
                        data-testid={`read-more-${post.id}`}
                      >
                        Read More
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section-spacing bg-[#aaae10] text-white">
        <div className="content-container text-center">
          <h2 className="font-rubik text-3xl font-bold mb-4">SUBSCRIBE TO OUR NEWSLETTER</h2>
          <p className="font-roboto text-lg mb-6 max-w-2xl mx-auto">
            Get the latest insights, project updates, and industry news delivered directly to
            your inbox.
          </p>
          <div className="max-w-md mx-auto flex gap-2">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded font-roboto text-black"
              data-testid="newsletter-email-input"
            />
            <button className="btn-secondary" data-testid="newsletter-submit-button">
              Submit
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
